import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  Globe,
  Clock,
  Twitter,
  Linkedin,
  Instagram,
  Github,
} from "lucide-react";
import { LOGO_URL } from "@/lib/site-config";

const SERVICE_LINKS = [
  "Website Development",
  "SEO & GEO",
  "Branding",
  "Maintenance",
  "Content Writing",
];

const QUICK_LINKS = [
  { label: "Home", href: "/" },
  { label: "Projects", href: "/projects" },
  { label: "Pricing", href: "/pricing" },
  { label: "Reviews", href: "/reviews" },
  { label: "About", href: "/about" },
  { label: "Team", href: "/team" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-[#050505] pt-20 pb-8 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <img
              src={LOGO_URL}
              alt="EchoWings Media"
              className="h-10 w-auto"
            />
            <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-xs">
              EchoWings Media is a premium digital growth studio building
              websites, SEO and AI visibility systems for ambitious businesses.
            </p>
            <div className="mt-6 flex items-center gap-2">
              {[
                { Icon: Twitter, label: "twitter" },
                { Icon: Linkedin, label: "linkedin" },
                { Icon: Instagram, label: "instagram" },
                { Icon: Github, label: "github" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-sm text-white/55 hover:text-cyan-400 transition-colors"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-sm text-white/55 hover:text-cyan-400 transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">
              Contact
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-white/55">
                <Mail className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>hello@echowingsmedia.com</span>
              </li>
              <li className="flex items-start gap-2 text-white/55">
                <Phone className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>+1 (555) 894-3210</span>
              </li>
              <li className="flex items-start gap-2 text-white/55">
                <Globe className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>echowingsmedia.com</span>
              </li>
              <li className="flex items-start gap-2 text-white/55">
                <Clock className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>Mon – Fri · 9:00 – 18:00 EST</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Massive watermark */}
        <div className="relative mt-20 mb-10 overflow-hidden">
          <div className="text-center font-display font-extrabold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white/[0.07] to-transparent text-[18vw] md:text-[14vw] leading-none select-none pointer-events-none">
            ECHOWINGS
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <div>
            © {new Date().getFullYear()} EchoWings Media. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-terms"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
